import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Zap, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../api/client';

function DevTools() {
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [simulatingIds, setSimulatingIds] = useState(new Set());

  useEffect(() => {
    fetchPaymentPlans();
  }, []);

  const fetchPaymentPlans = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/payment-plans?limit=20');
      
      const plansWithParts = await Promise.all(
        response.data.data.map(async (plan) => {
          const partsResponse = await apiClient.get(`/payment-plans/${plan.id}`);
          return partsResponse.data.data;
        })
      );
      
      setPaymentPlans(plansWithParts);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar planos de pagamento:', err);
      setError('Falha ao carregar planos de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const simulatePayment = async (ipagPaymentId, partId) => {
    try {
      setSimulatingIds(prev => new Set(prev).add(partId));
      
      await apiClient.post(`/dev/simulate-payment/${ipagPaymentId}`);
      
      await fetchPaymentPlans();
      
      setSimulatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(partId);
        return newSet;
      });
    } catch (err) {
      console.error('Erro ao simular pagamento:', err);
      alert(`Falha ao simular pagamento: ${err.response?.data?.error || err.message}`);
      setSimulatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(partId);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'FAILED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase() || 'PENDING';
    const colors = {
      PAID: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[statusUpper] || 'bg-gray-100 text-gray-800'}`}>
        {statusUpper}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Carregando planos de pagamento...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-yellow-900">Modo de Teste Sandbox</h3>
            <p className="text-sm text-yellow-800 mt-1">
              Este painel só é visível em desenvolvimento. Use os botões "Simular Pagamento" para simular capturas de pagamento iPag para testar pedidos com múltiplos pagamentos.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Planos de Pagamento (Teste Dev)</h2>
        <button
          onClick={fetchPaymentPlans}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Atualizar
        </button>
      </div>

      {paymentPlans.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Nenhum plano de pagamento encontrado. Crie um primeiro!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paymentPlans.map((plan) => (
            <div key={plan.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Pedido #{plan.order_number}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      ID do Plano: {plan.id} • Total: {plan.currency} {parseFloat(plan.order_total).toFixed(2)}
                    </p>
                  </div>
                  {getStatusBadge(plan.status)}
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  {plan.parts?.map((part) => (
                    <div
                      key={part.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {getStatusIcon(part.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900">{part.label}</span>
                            {getStatusBadge(part.status)}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {part.method} • {plan.currency} {parseFloat(part.amount).toFixed(2)}
                            {part.installments && ` • ${part.installments}x`}
                          </div>
                          {part.ipag_payment_id && (
                            <div className="text-xs text-gray-500 mt-1 font-mono">
                              iPag ID: {part.ipag_payment_id}
                            </div>
                          )}
                        </div>
                      </div>

                      {part.status?.toUpperCase() !== 'PAID' && part.ipag_payment_id && (
                        <button
                          onClick={() => simulatePayment(part.ipag_payment_id, part.id)}
                          disabled={simulatingIds.has(part.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Zap className="w-4 h-4" />
                          {simulatingIds.has(part.id) ? 'Simulando...' : 'Simular Pagamento'}
                        </button>
                      )}

                      {part.status?.toUpperCase() === 'PAID' && (
                        <div className="flex items-center gap-2 text-green-600 font-medium">
                          <CheckCircle className="w-5 h-5" />
                          Pago
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DevTools;
